import GetLinkButton from "@/Components/GetLinkButton";
import Image from "@/Components/Image";
import { GlobalContext } from "@/Context/GlobalProvider";
import { copyToClipboard } from "@/helpers/functions";
import { useContext, useMemo, useState } from "react";
import { toast } from "react-toastify";

export default function PropertyCardMini({ property, tab }) {
    const { favorites, toggleFavorite } = useContext(GlobalContext);
    const isFavorite = useMemo(
        () => favorites.includes(property.reference),
        [favorites]
    );
    const [visible, setVisible] = useState(true);

    const handleForwardClick = () => {
        setVisible(false);
        setTimeout(() => {
            toggleFavorite(property.reference);
        }, 300);
    };

    const handleCopy = (url) => {
        const toastId = toast.loading("Copying link...", {
            position: "bottom-right",
        });
        copyToClipboard(url);
        toast.update(toastId, {
            render: "Link copied successfully!",
            type: "success",
            isLoading: false,
            autoClose: 1500,
        });
    };

    return (
        <article
            className={`flex gap-3 lg:gap-10 transition-opacity duration-1000 ${
                !visible ? "opacity-0" : ""
            }`}
        >
            <a href={property?.url} target="_blank">
                <Image
                    image={property.featured_image}
                    size="small"
                    alt={
                        property?.featured_image
                            ? property?.featured_image.alt
                            : property?.title
                    }
                    className="aspect-square sm:aspect-[4/3] w-full max-w-16 md:max-w-36 lg:max-w-52 rounded-md object-center object-cover"
                />
            </a>
            <div className="flex-1 flex items-center gap-3 lg:gap-10">
                <div className="flex-1 lg:space-y-7">
                    <div className="flex items-center justify-between gap-3">
                        <a href={property?.url} target="_blank" translate="no" lang="en">
                            <h3 className="text-base sm:text-2xl line-clamp-2 font-bold">
                                {property.title}
                            </h3>
                        </a>
                        <div className="hidden lg:flex">
                            {tab !== "share" && (
                                <GetLinkButton url={property.url} />
                            )}
                        </div>
                    </div>
                    <div className="flex justify-between items-end text-dark">
                        <div>
                            <div className="sm:text-lg font-semibold">
                                {property.price}
                            </div>
                            <div className="max-lg:hidden text-sm">
                                PRICE {property.is_development ? " FROM" : ""}
                            </div>
                        </div>
                        {property.plot_size && (
                            <div className=" max-lg:hidden text-center">
                                {property.is_development ? (
                                    <div className="text-sm">FROM</div>
                                ) : (
                                    ""
                                )}
                                <div className="text-lg font-semibold">
                                    {property.plot_size}
                                </div>
                                <div className="text-sm">M2 PLOT</div>
                            </div>
                        )}
                        {property.built_size && (
                            <div className=" max-lg:hidden text-center">
                                {property.is_development ? (
                                    <div className="text-sm">FROM</div>
                                ) : (
                                    ""
                                )}
                                <div className="text-lg font-semibold">
                                    {property.built_size}
                                </div>
                                <div className="text-sm">M2 INT</div>
                            </div>
                        )}
                        {property.bedrooms && (
                            <div className=" max-lg:hidden text-center">
                                {property.is_development ? (
                                    <div className="text-sm">FROM</div>
                                ) : (
                                    ""
                                )}
                                <div className="text-lg font-semibold">
                                    {property.bedrooms}
                                </div>
                                <div className="text-sm">BEDS</div>
                            </div>
                        )}
                        {property.bathrooms && (
                            <div className=" max-lg:hidden text-center">
                                {property.is_development ? (
                                    <div className="text-sm">FROM</div>
                                ) : (
                                    ""
                                )}
                                <div className="text-lg font-semibold">
                                    {property.bathrooms}
                                </div>
                                <div className="text-sm">BATHS</div>
                            </div>
                        )}
                    </div>
                </div>
                {tab !== "share" && (
                    <div className="sm:max-w-28 sm:w-full flex justify-center items-center gap-2">
                        <button
                            onClick={() => handleCopy(property.url)}
                            className="size-10 sm:size-12 flex justify-center items-center lg:hidden"
                        >
                            <svg
                                className="size-6"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.8284 10.1716C12.2663 8.60948 9.73367 8.60948 8.17157 10.1716L4.17157 14.1716C2.60948 15.7337 2.60948 18.2663 4.17157 19.8284C5.73367 21.3905 8.26633 21.3905 9.82843 19.8284L10.93 18.7269M10.1716 13.8284C11.7337 15.3905 14.2663 15.3905 15.8284 13.8284L19.8284 9.82843C21.3905 8.26633 21.3905 5.73367 19.8284 4.17157C18.2663 2.60948 15.7337 2.60948 14.1716 4.17157L13.072 5.27118"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <button
                            onClick={() => {
                                handleForwardClick(property.reference);
                            }}
                            className="size-8 sm:size-12 flex justify-center items-center"
                        >
                            {!isFavorite ? (
                                <svg
                                    className="size-6"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="190 -820 600 650"
                                    fill="currentColor"
                                >
                                    <path
                                        fill="currentColor"
                                        d="M240-180v-555.38q0-27.62 18.5-46.12Q277-800 304.62-800h350.76q27.62 0 46.12 18.5Q720-763 720-735.38V-180L480-283.08 240-180Zm40-62 200-86 200 86v-493.38q0-9.24-7.69-16.93-7.69-7.69-16.93-7.69H304.62q-9.24 0-16.93 7.69-7.69 7.69-7.69 16.93V-242Zm0-518h400-400Z"
                                    />
                                </svg>
                            ) : (
                                <svg
                                    className="size-6"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="190 -820 600 650"
                                    fill="currentColor"
                                >
                                    <path d="M240-180v-555.38q0-27.62 18.5-46.12Q277-800 304.62-800h350.76q27.62 0 46.12 18.5Q720-763 720-735.38V-180L480-283.08 240-180Z" />
                                </svg>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </article>
    );
}
